import { MessageService } from 'src/app/modules/message/services/message/message.service';
import { LoggingService } from 'src/app/shared/services/logging/logging.service';
import { MessageCodes } from 'src/app/shared/enums';

export const MAX_RETRY_ATTEMPTS = 3;
export const INITIAL_RETRY_DELAY = 2;

/* eslint-disable @typescript-eslint/no-empty-object-type */
export type HttpParams = { [index: string]: string | number };
export type HttpHeaders = { [key: string]: string };
export type HttpMethods = 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  params?: HttpParams;
  headers?: HttpHeaders;
}

export interface RequestOptionsWithoutData extends RequestOptions {
  ccoVersion?: number;
  crc?: number;
  modules?: Record<string, unknown>;
  removeSet?: string[] | null;
  userId?: number;
}

export interface RequestOptionsWithData<T extends Record<string, unknown>>
  extends RequestOptions {
  data?: T;
}

export interface FetchConfig {
  baseUrl?: string;
  defaultHeaders?: HttpHeaders;
  csrfCookieName?: string;
  csrfHeaderName?: string;
  trailingSlash?: TrailingSlashOptions;
}

export type TrailingSlashOptions = 'always' | 'none' | 'write_only';

/**
 * Library loosely adapted from the premise in ngx-fetch-api
 */
export class APIHandler {
  baseUrl = '';
  endpoint?: string;
  failSilently = false;
  private defaultHeaders: HttpHeaders = {};
  private trailingSlash: TrailingSlashOptions = 'write_only';
  private _messageService!: MessageService;
  private _loggingService?: LoggingService;
  constructor(
    config?: FetchConfig,
    private configId = 'idsrv'
  ) {
    if (config) {
      this.configure(config);
    }
  }

  set messageService(messageService: MessageService) {
    this._messageService = messageService;
  }

  set loggingService(loggingService: LoggingService) {
    this._loggingService = loggingService;
  }

  configure(config: FetchConfig): void {
    if (config.baseUrl) {
      this.baseUrl = config.baseUrl.endsWith('/')
        ? config.baseUrl.slice(0, -1)
        : config.baseUrl;
    }

    if (config.defaultHeaders) {
      this.defaultHeaders = config.defaultHeaders;
    }

    if (config.trailingSlash) {
      this.trailingSlash = config.trailingSlash;
    }
  }

  getHeaders(headers?: HttpHeaders): HttpHeaders {
    const newHeaders = {
      ...this.defaultHeaders,
      ...headers,
    };

    const authTokenSessionStorage = this.getAuthTokenFromSessionStorage();
    if (authTokenSessionStorage) {
      newHeaders.Authorization = `Bearer ${authTokenSessionStorage}`;
    }

    return newHeaders;
  }

  getAuthTokenFromSessionStorage(): string {
    const idsrvSess = sessionStorage.getItem(this.configId);
    if (idsrvSess) {
      const sessJSON = JSON.parse(idsrvSess);
      const authToken = sessJSON.authnResult.access_token;
      if (authToken) {
        return authToken;
      }
    }

    return '';
  }

  async request<TResponse extends {} | void>(
    method: HttpMethods,
    path: string,
    options?:
      | RequestOptionsWithoutData
      | RequestOptionsWithData<Partial<TResponse>>
      | RequestOptions,
    attempt = 0
  ): Promise<TResponse> {
    const fetchOptions: { [key: string]: unknown } = { method };
    const headers: { [key: string]: unknown } =
      this.getHeaders(options?.headers) || {};
    const url: string = ((): string => {
      let url = path.startsWith('/') ? path : `${this.baseUrl}/${path}`;
      if (!path.endsWith('/')) {
        const ts = this.trailingSlash;
        if (ts === 'always' || (ts === 'write_only' && method !== 'GET')) {
          path = path + '/';
        }
      }
      if (options && options.params && Object.keys(options.params).length > 0) {
        url +=
          '?' +
          Object.entries(options.params)
            .reduce((acc, [k, v]) => {
              k &&
                v !== null &&
                typeof v !== 'undefined' &&
                acc.push(
                  `${encodeURIComponent(k)}=${encodeURIComponent('' + v)}`
                );
              return acc;
            }, [] as string[])
            .join('&');
      }
      return url;
    })();

    if ((<RequestOptionsWithData<Partial<TResponse>>>options)?.data) {
      fetchOptions['body'] = JSON.stringify(
        (<RequestOptionsWithData<Partial<TResponse>>>options).data
      );
    }
    // some requests exclude 'data:' as the root key
    else {
      fetchOptions['body'] = JSON.stringify(<RequestOptionsWithoutData>options);
    }

    if (fetchOptions['body']) {
      headers['Content-Type'] = 'application/json';
      if (method === 'GET') {
        headers['Cache-Control'] = 'no-cache';
      }
    }

    fetchOptions['headers'] = headers;
    return fetch(url, fetchOptions)
      .then(response => {
        const contentType = response.headers.get('Content-Type');
        const isJson = contentType?.includes('application/json');
        if (this.hasValidResponse(response)) {
          return isJson ? response.json() : null;
        } else if (response.status === 400 && isJson) {
          return response.json().then(json => {
            throw json;
          });
        } else if (response.status >= 500 && response.status !== 504) {
          return this.retryOrFail(method, path, options, response, attempt);
        }
        throw response;
      })
      .catch(error => {
        // Network failures (e.g. blocked requests, DNS errors) reject with TypeError
        if (
          error instanceof TypeError &&
          error.message.includes('Failed to fetch')
        ) {
          return this.retryOrFail(method, path, options, error, attempt);
        }
        throw error;
      })
      .then(json => {
        if (this.hasApiMessage(json)) {
          this.logQueryFailure(method, path, json.message);
          throw json.message;
        } else {
          return json;
        }
      });
  }

  private retryOrFail<TResponse extends {} | void>(
    method: HttpMethods,
    path: string,
    options:
      | RequestOptionsWithoutData
      | RequestOptionsWithData<Partial<TResponse>>
      | RequestOptions
      | undefined,
    error: unknown,
    attempt: number
  ): Promise<TResponse> {
    // Retry up to MAX_RETRY_ATTEMPTS times with exponential backoff.
    // Delays: 2s, 4s, 8s (INITIAL_RETRY_DELAY * 2^attempt).
    if (attempt >= MAX_RETRY_ATTEMPTS) {
      if (!this.failSilently) {
        this._messageService?.showMessage(
          MessageCodes.HTTP_UNEXPECTED_NET_ERROR
        );
      }
      this.logQueryFailure(method, path, error);
      throw error;
    }
    const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt);
    console.warn(
      `Retrying request ${method} ${path} (attempt ${
        attempt + 1
      }/${MAX_RETRY_ATTEMPTS}) in ${delay}s`
    );

    return new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          const res = await this.request(method, path, options, attempt + 1);
          resolve(res);
        } catch (e) {
          reject(e);
        }
      }, delay * 1000);
    });
  }

  async get<TResponse extends {}>(
    path: string,
    options?: RequestOptions
  ): Promise<TResponse> {
    return await this.request<TResponse>('GET', path, options);
  }

  post<TResponse extends {}>(
    path: string,
    options?: RequestOptionsWithoutData | RequestOptionsWithData<TResponse>
  ): Promise<TResponse> {
    return this.request<TResponse>('POST', path, options);
  }

  put<TResponse extends {}>(
    path: string,
    options?: RequestOptionsWithoutData | RequestOptionsWithData<TResponse>
  ): Promise<TResponse> {
    return this.request<TResponse>('PUT', path, options);
  }

  patch<TResponse extends {}>(
    path: string,
    options?:
      | RequestOptionsWithoutData
      | RequestOptionsWithData<Partial<TResponse>>
  ): Promise<TResponse> {
    return this.request<TResponse>('PATCH', path, options);
  }

  delete(path: string, options?: RequestOptions): Promise<void> {
    return this.request<void>('DELETE', path, options);
  }

  private logQueryFailure(
    method: HttpMethods,
    path: string,
    error: unknown
  ): void {
    const isResponse = error instanceof Response;
    this._loggingService?.logInfo('api_query_failed', {
      method,
      path,
      errorMessage:
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : isResponse
              ? `${error.status} ${error.statusText}`
              : 'Unknown error',
      errorStatus: isResponse ? error.status : null,
      url: `${this.baseUrl}/${path}`,
    });
  }

  private hasValidResponse(apiResponse: Response): boolean {
    return (
      apiResponse &&
      apiResponse.ok &&
      apiResponse.status >= 200 &&
      apiResponse.status < 300 &&
      !!apiResponse.body
    );
  }

  private hasApiMessage<T>(apiResponse: T | null): boolean {
    return (
      !!apiResponse &&
      Object.prototype.hasOwnProperty.call(apiResponse, 'message')
    );
  }
}
